import { StudentAvatar } from '@/components/shared/StudentAvatar/StudentAvatar';
import { Button } from '@/components/ui/Button/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/DropdownMenu/DropdownMenu';
import { useTimeFor } from '@/hooks/useTimeFor';
import { useAuthStore } from '@/stores/authStore';
import type { Appointment, RecurringSchedule, SchedulingEnrollment } from '@/types/scheduling';
import { cn } from '@/utils';
import { Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export type AppointmentData = Pick<Appointment, 'student_id' | 'appointment_date' | 'duration_minutes' | 'enrollment_id'> & {
  recurring_schedule?: Pick<RecurringSchedule, 'recurrence_pattern' | 'effective_until'> | null;
};

interface SessionSummaryProps {
  appointment: AppointmentData;
  enrollment?: SchedulingEnrollment;
  onDelete?: () => void;
  className?: string;
  portalContainerRef?: React.RefObject<HTMLElement | null>;
}

export const SessionSummary = ({ appointment, enrollment, onDelete, className, portalContainerRef }: SessionSummaryProps) => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'sessionSummary' });
  const { t: tInterval } = useTranslation('scheduling', { keyPrefix: 'interval' });

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { format, formatWithZone, formatDate } = useTimeFor('bookingConfirmation');

  const sessionDate = new Date(appointment.appointment_date);
  const recurrenceEndDate = appointment.recurring_schedule ? new Date(appointment.recurring_schedule.effective_until) : null;

  const { userData, studentsBgColors } = useAuthStore();

  const userDataStudent = userData?.students.find((student) => student.id === appointment.student_id) ?? {
    id: appointment.student_id,
    firstName: 'Unknown',
    lastName: 'Unknown',
  };

  return (
    <div className={cn('bg-fill-background-light flex flex-col gap-4 rounded-lg p-5 pb-6', className)}>
      <div className='flex items-center justify-between gap-2.5'>
        <div className='flex items-center gap-2.5'>
          <StudentAvatar student={{ ...userDataStudent, bgColor: studentsBgColors.get(userDataStudent.id) ?? '#fff' }} className='size-10' />
          <span className='text-body-m-regular text-text-default-secondary max-w-[250px] overflow-hidden text-ellipsis'>
            {userDataStudent.firstName} {userDataStudent.lastName}
          </span>
        </div>
        {onDelete && (
          <DropdownMenu onOpenChange={setIsMenuOpen} open={isMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant='icon'
                size='icon-l'
                aria-label={
                  isMenuOpen
                    ? t('removeMenu.ariaLabelClose', { studentName: userDataStudent.firstName })
                    : t('removeMenu.ariaLabelOpen', { studentName: userDataStudent.firstName })
                }
              >
                {isMenuOpen ? <X className='text-text-default-secondary' /> : <Trash2 className='text-text-default-secondary' />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align='end'
              portalContainer={portalContainerRef?.current}
              onKeyDown={(event) => {
                if (event.key === 'Tab') {
                  setIsMenuOpen(false);
                }
              }}
            >
              <DropdownMenuItem onClick={onDelete} className='cursor-pointer'>
                <Trash2 className='text-text-default-secondary mr-2' size={20} />
                <span className='text-body-m-regular'>{t('removeMenu.tooltip')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div>
        <p className='text-heading-s-medium mb-3'>
          {enrollment?.name ?? t('unknownEnrollment')} ({appointment.duration_minutes} {t('minutes')})
        </p>
        <time className='text-text-default-secondary' dateTime={sessionDate.toISOString()}>
          {formatWithZone(sessionDate, t('dateTimeFormat'))}
        </time>
        {recurrenceEndDate && appointment.recurring_schedule && (
          <div className='text-text-default-secondary'>
            <span>
              {tInterval(appointment.recurring_schedule.recurrence_pattern)} {t('on')} {format(sessionDate, 'EEEE')},{' '}
            </span>
            {t('until')} <time dateTime={recurrenceEndDate.toISOString()}>{formatDate(recurrenceEndDate)}</time>
          </div>
        )}
      </div>
    </div>
  );
};
